import { useState, useEffect, useRef } from "react";
import { ChevronRight, ChevronLeft, Send } from "lucide-react";
import { useAnalyticsQuery } from "@/hooks/useAnalyticsQuery";
import { analyticsApi, ApiError } from "@/services/apiClient";
import { useFilterStore, isReadOnlyEnv } from "@/stores/filterStore";
import { fmt } from "@/lib/format";
import { newReplyId } from "@/lib/cxReply";

// 모바일 전용 오류 제보/문의 — 조회 중심 카드 리스트(P2, 2026-09-17).
// desktop CxSection 의 3단 헬프데스크 콘솔을 모바일에 그대로 넣지 않고, 같은 API
// (analyticsApi.cx.tickets)만 재사용해 상태 필터 + 카드 + 상세(스레드 읽기)로 재구성.
// 답변/상태변경은 desktop 권장(모바일 오탭·복잡한 스레드 편집 회피) — 상세에서 안내만.
const STATE_META = {
  open:     { label: "열림", cls: "bg-red-50 text-red-600 border-red-200" },
  answered: { label: "답변완료", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  closed:   { label: "닫힘", cls: "bg-ink-100 text-ink-500 border-ink-200" },
};
const FILTERS = [
  { id: "open", label: "미처리" },     // 서버 state=open
  { id: "answered", label: "처리중" }, // 답변 완료(후속 대기)
  { id: "closed", label: "완료" },
  { id: "all", label: "전체" },
];

function fmtRel(iso) {
  if (!iso) return "";
  try { return fmt.kstDateTime(iso); } catch { return iso; }
}

export default function MobileCxList() {
  const [filter, setFilter] = useState("open");
  const [selected, setSelected] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // desktop 과 동일하게 state=all 로 받아 클라이언트에서 필터(탭 전환 시 재조회 없음).
  const { data, loading, error } = useAnalyticsQuery(
    (p, s) => analyticsApi.cx.tickets({ state: "all" }, s),
    [refreshTick]
  );

  const all = data?.tickets || data?.items || [];
  const tickets = filter === "all" ? all : all.filter((t) => (t.status || "open") === filter);

  if (selected) {
    const live = all.find((t) => t.id === selected.id) || selected;
    return <MobileTicketDetail ticket={live} onBack={() => setSelected(null)} onChanged={() => setRefreshTick((t) => t + 1)} />;
  }

  return (
    <div className="space-y-3">
      {/* 상태 필터 — 1줄 */}
      <div className="flex gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {FILTERS.map((f) => {
          const count = f.id === "all" ? all.length : all.filter((t) => (t.status || "open") === f.id).length;
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`shrink-0 rounded-full border px-3 py-1.5 text-[12px] font-medium ${
                active ? "border-brand-500 bg-brand-600 text-white" : "border-ink-200 bg-white text-ink-600"
              }`}
            >
              {f.label}{count > 0 && <span className="ml-1 tabular-nums opacity-80">{count}</span>}
            </button>
          );
        })}
      </div>

      {loading && !data && <div className="py-8 text-center text-sm text-ink-400">불러오는 중…</div>}
      {error && <div className="py-8 text-center text-sm text-red-600">불러오지 못했습니다 — {error.message}</div>}
      {!loading && tickets.length === 0 && <div className="py-8 text-center text-sm text-ink-400">해당 상태의 제보가 없습니다.</div>}

      <div className="space-y-2">
        {tickets.map((t) => {
          const meta = STATE_META[t.status] || STATE_META.open;
          const summary = (t.messages?.[0]?.text || t.summary || t.title || "").trim();
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelected(t)}
              className="flex w-full items-start gap-3 rounded-xl border border-ink-100 bg-white px-3.5 py-3 text-left"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${meta.cls}`}>{meta.label}</span>
                  {t.severity && t.severity !== "normal" && (
                    <span className="shrink-0 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">중요</span>
                  )}
                  <span className="ml-auto shrink-0 text-[11px] text-ink-400">{fmtRel(t.updatedAt || t.createdAt)}</span>
                </div>
                <div className="mt-1 truncate text-[13px] font-semibold text-ink-800">{t.email || "익명"}</div>
                {summary && <div className="mt-0.5 line-clamp-2 text-[12px] text-ink-500">{summary}</div>}
              </div>
              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-ink-300" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MobileTicketDetail({ ticket, onBack, onChanged }) {
  const t = ticket;
  const meta = STATE_META[t.status] || STATE_META.open;
  const msgs = t.messages || [];
  const readOnly = isReadOnlyEnv(useFilterStore((s) => s.env));

  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // 답변 1건의 멱등 키 — 실패 시 유지해 같은 키로 재시도(중복 저장·중복 이메일 방지).
  const replyIdRef = useRef(null);

  const STATUS_OPTS = [
    { id: "open", label: "미처리" },
    { id: "answered", label: "처리중" },
    { id: "closed", label: "완료" },
  ];

  async function changeStatus(status) {
    if (readOnly) { setErr("STG 조회 상태에서는 상태 변경이 비활성화됩니다."); return; }
    if (busy || status === t.status) return;
    setBusy(true); setErr(null);
    try {
      await analyticsApi.cx.ticketUpdate(t.id, { status });
      onChanged?.();
    } catch (e) { setErr(e instanceof ApiError ? e.message : "상태 변경 실패"); }
    finally { setBusy(false); }
  }

  async function sendReply() {
    if (readOnly) { setErr("STG 조회 상태에서는 답변 전송이 비활성화됩니다."); return; }
    const body = reply.trim();
    if (!body) { setErr("답변 내용을 입력하세요."); return; }
    if (busy) return;
    const replyId = replyIdRef.current || (replyIdRef.current = newReplyId());
    setBusy(true); setErr(null);
    try {
      await analyticsApi.cx.ticketUpdate(t.id, { reply: body, replyId });
      replyIdRef.current = null;      // 성공 — 다음 답변은 새 키
      setReply("");
      onChanged?.();
    } catch (e) { setErr(e instanceof ApiError ? e.message : "전송 실패"); }
    finally { setBusy(false); }
  }

  return (
    <div className="flex min-h-full flex-col">
      <div className="flex-1 space-y-4 pb-4">
        <button type="button" onClick={onBack} className="flex items-center gap-1 text-[13px] font-medium text-ink-500">
          <ChevronLeft className="h-4 w-4" /> 목록으로
        </button>

        <div className="rounded-xl border border-ink-100 bg-white p-4">
          <div className="flex items-center gap-2">
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${meta.cls}`}>{meta.label}</span>
            <span className="ml-auto text-[11px] text-ink-400">접수 {fmtRel(t.createdAt)}</span>
          </div>
          <div className="mt-1.5 truncate text-[15px] font-bold text-ink-900">{t.email || "익명"}</div>
          {t.userId && <div className="mt-0.5 break-all font-mono text-[11px] text-ink-400">{t.userId}</div>}
        </div>

        {/* 스레드(읽기) */}
        <div className="space-y-2">
          {msgs.length === 0 && <div className="rounded-xl border border-ink-100 bg-white px-4 py-3 text-[13px] text-ink-500">메시지가 없습니다.</div>}
          {msgs.map((m, i) => (
            <div key={i} className={`rounded-xl border px-3.5 py-2.5 ${m.from === "user" ? "border-ink-100 bg-white" : "border-brand-100 bg-brand-50/50"}`}>
              <div className="mb-1 text-[11px] font-medium text-ink-400">{m.from === "user" ? "고객" : "운영자"} · {fmtRel(m.at || m.createdAt)}</div>
              <div className="whitespace-pre-wrap break-words text-[13px] text-ink-800">{m.text}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 하단 sticky 액션 — 상태 변경 + 답변. 긴 스레드에서도 접근. */}
      <div className="sticky bottom-0 -mx-3 border-t border-ink-100 bg-white px-3 pb-[calc(0.5rem+env(safe-area-inset-bottom))] pt-2 sm:-mx-6 sm:px-6">
        {readOnly ? (
          <div className="rounded-lg bg-ink-100 px-3 py-2 text-center text-[12px] font-medium text-ink-500">STG 조회 전용 — 답변·상태 변경 비활성화</div>
        ) : (
          <>
            <div className="mb-1.5 flex gap-1.5">
              {STATUS_OPTS.map((s) => (
                <button key={s.id} type="button" disabled={busy} onClick={() => changeStatus(s.id)}
                  className={`h-11 flex-1 rounded-lg border text-[12px] font-semibold disabled:opacity-50 ${t.status === s.id ? "border-brand-500 bg-brand-600 text-white" : "border-ink-200 bg-white text-ink-600"}`}>
                  {s.label}
                </button>
              ))}
            </div>
            <div className="flex items-end gap-2">
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={1}
                placeholder="답변 입력…"
                className="max-h-28 min-h-[44px] flex-1 resize-none rounded-xl border border-ink-200 px-3 py-2.5 text-sm text-ink-800 focus:border-brand-400 focus:outline-none"
              />
              <button type="button" onClick={sendReply} disabled={busy || !reply.trim()} aria-label="답변 전송"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-600 text-white disabled:opacity-50">
                <Send className="h-4 w-4" />
              </button>
            </div>
            {err && <div className="mt-1 text-[12px] text-red-600">{err}</div>}
          </>
        )}
      </div>
    </div>
  );
}
